import { toast } from 'sonner';
import type { CustodyCase, NoteSheet } from '../types/case';
import type { AdminRole } from '../types/config';
import { useStore } from '../contexts/StoreContext';
import { ADMIN_USERS } from '../data/users';
import { isPaid, letterBlockers } from '../utils/caseStatus';
import { generateNote } from '../utils/noteSheet';
import { bn, nowIso, randomDigits } from '../utils/bn';

const QR_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const qrToken = (pcr: string) => `RL${pcr.slice(-4)}-${Array.from({ length: 6 }, () => QR_CHARS[Math.floor(Math.random() * QR_CHARS.length)]).join('')}`;

/** Role-attributed workflow transitions shared across the web portal. */
export function useCaseActions(role: AdminRole) {
  const { updateCase, getCase, config } = useStore();
  const user = ADMIN_USERS[role];
  const by = { actor: user.name, role: user.title };
  const approver = config.approvalLevels.find((l) => l.role === 'approver' && l.enabled);
  const signatory = config.approvalLevels.find((l) => l.role === 'signatory' && l.enabled);

  const promote = (c: CustodyCase): CustodyCase =>
    c.verification && isPaid(c) && c.docStatus === 'verified' && c.approval === 'none' ? { ...c, approval: 'drafting' } : c;

  return {
    verifyDocs(pcr: string, remarks: string) {
      updateCase(pcr, (c) => promote({
        ...c, docStatus: 'verified',
        documents: c.documents.map((d) => (d.fileName ? { ...d, status: 'verified', reason: undefined } : d)),
        verification: { officer: user.name, remarks: remarks || 'সকল কাগজপত্র যাচাইকৃত।', at: nowIso() },
      }), { ...by, key: 'verified', action: 'কাগজপত্র যাচাই সম্পন্ন', note: remarks || undefined });
      const c = getCase(pcr);
      toast.success(c && isPaid(c) ? 'যাচাই সম্পন্ন — নোটশিট প্রস্তুতির জন্য প্রস্তুত।' : 'যাচাই সম্পন্ন — জরিমানা পরিশোধ অপেক্ষমাণ।');
    },
    returnDocs(pcr: string, reasons: Record<string, string>, remarks: string) {
      updateCase(pcr, (c) => ({
        ...c, docStatus: 'correction', verification: undefined,
        documents: c.documents.map((d) => (reasons[d.key] ? { ...d, status: 'correction', reason: reasons[d.key] } : d)),
      }), { ...by, key: 'docs_returned', action: 'কাগজপত্র সংশোধনের জন্য মালিকের নিকট ফেরত', note: remarks || Object.values(reasons).join('; ') });
      toast.success('মালিককে সংশোধনের বিজ্ঞপ্তি পাঠানো হয়েছে (সিমুলেটেড)।');
    },
    requestExtra(pcr: string, text: string) {
      updateCase(pcr, (c) => ({
        ...c, docStatus: 'correction',
        documents: c.documents.some((d) => d.key === 'other')
          ? c.documents.map((d) => (d.key === 'other' ? { ...d, status: 'correction', reason: text } : d))
          : [...c.documents, { key: 'other', name: 'অন্যান্য প্রযোজ্য কাগজপত্র', required: true, status: 'correction', reason: text }],
      }), { ...by, key: 'docs_returned', action: 'অতিরিক্ত কাগজপত্র চাওয়া হয়েছে', note: text });
      toast.success('অতিরিক্ত কাগজপত্রের অনুরোধ পাঠানো হয়েছে।');
    },
    sendNext(pcr: string): boolean {
      const c = getCase(pcr);
      if (!c) return false;
      if (c.docStatus !== 'verified' || !c.verification) { toast.error('কাগজপত্র যাচাই সম্পন্ন হয়নি।'); return false; }
      if (!isPaid(c)) { toast.error('জরিমানা পরিশোধ নিশ্চিত হয়নি — পরবর্তী ধাপে পাঠানো যাবে না।'); return false; }
      updateCase(pcr, (x) => ({ ...x, approval: x.approval === 'none' ? 'drafting' : x.approval }), { ...by, key: 'comment', action: 'নথি প্রস্তুতকারী কর্মকর্তার নিকট প্রেরিত' });
      toast.success('নোটশিট প্রস্তুতির জন্য নথি প্রস্তুতকারীর নিকট পাঠানো হয়েছে।');
      return true;
    },
    recordOfficeDocs(pcr: string, keys: string[], date: string, remarks: string) {
      updateCase(pcr, (c) => ({
        ...c, docStatus: 'verifying', ownerLinked: true,
        documents: c.documents.map((d) => (keys.includes(d.key) ? { ...d, status: 'verifying', source: 'office', fileType: 'pdf', fileName: `${d.key}_office_scan.pdf`, uploadedAt: date, reason: undefined } : d)),
      }), { ...by, key: 'office_docs', action: `সরাসরি অফিসে ${bn(keys.length)}টি কাগজপত্র গ্রহণ ও স্ক্যান কপি সংযুক্ত`, note: remarks || undefined });
      toast.success('অফিসে জমাকৃত কাগজপত্র PCR-এর সাথে যুক্ত হয়েছে।');
    },
    reconcile(pcr: string) {
      updateCase(pcr, (c) => promote({ ...c, paymentStatus: 'reconciled', payment: { ...c.payment, reconciledAt: nowIso(), reconciledBy: user.name } }),
        { ...by, key: 'reconciled', action: 'পেমেন্ট সমন্বিত' });
      toast.success('পেমেন্ট সমন্বয় সম্পন্ন।');
    },
    flagPayment(pcr: string, note: string) {
      updateCase(pcr, (c) => ({ ...c, paymentStatus: 'correction' }), { ...by, key: 'comment', action: 'পেমেন্ট সংশোধন প্রয়োজন হিসেবে চিহ্নিত', note });
      toast.success('পেমেন্ট সংশোধনের জন্য চিহ্নিত হয়েছে।');
    },
    recordOfficePayment(pcr: string, p: { method: string; challan: string; date: string; officer: string }) {
      updateCase(pcr, (c) => promote({
        ...c, paymentStatus: 'reconciled',
        payment: { channel: p.method, challan: p.challan, paidAt: p.date, source: 'office', officer: p.officer, reconciledAt: nowIso(), reconciledBy: user.name },
      }), { ...by, key: 'office_payment', action: `অফিসে জরিমানা গ্রহণ — চালান ${p.challan}` });
      toast.success('অফিস পেমেন্ট লিপিবদ্ধ ও সমন্বিত হয়েছে।');
    },
    saveNote(pcr: string, note: NoteSheet) {
      updateCase(pcr, (c) => ({ ...c, noteSheet: { ...note, savedAt: nowIso() }, approval: c.approval === 'none' ? 'drafting' : c.approval }),
        { ...by, key: 'note_saved', action: 'নোটশিট খসড়া সংরক্ষণ' });
      toast.success('খসড়া নোটশিট সংরক্ষিত হয়েছে।');
    },
    forwardNote(pcr: string, note: NoteSheet, remark: string) {
      const at = nowIso();
      updateCase(pcr, (c) => ({
        ...c, approval: 'pending', approvalComment: undefined,
        noteSheet: { ...note, savedAt: at, forwards: [...note.forwards, { at, from: user.name, fromRole: user.title, to: approver?.officer ?? 'অনুমোদনকারী', action: 'অগ্রায়ন', remark: remark || 'সদয় অনুমোদনের জন্য পেশ করা হলো।' }] },
      }), { ...by, key: 'forwarded', action: 'নোটশিট অনুমোদনের জন্য অগ্রায়ন', note: remark || undefined });
      toast.success(`নথি ${approver?.officer ?? 'অনুমোদনকারী'}-এর নিকট অগ্রায়ন করা হয়েছে।`);
    },
    decide(pcr: string, decision: 'approved' | 'returned' | 'rejected' | 'held', comment: string) {
      const labels = { approved: 'অনুমোদিত', returned: 'সংশোধনের জন্য ফেরত', rejected: 'প্রত্যাখ্যাত', held: 'স্থগিত' } as const;
      const to = decision === 'approved' ? signatory?.officer ?? 'স্বাক্ষরকারী' : config.approvalLevels.find((l) => l.role === 'dealing')?.officer ?? 'নথি প্রস্তুতকারী';
      const at = nowIso();
      updateCase(pcr, (c) => ({
        ...c, approval: decision, approvalComment: comment || undefined,
        noteSheet: c.noteSheet ? { ...c.noteSheet, forwards: [...c.noteSheet.forwards, { at, from: user.name, fromRole: user.title, to, action: labels[decision], remark: comment || labels[decision] }] } : c.noteSheet,
      }), { ...by, key: decision === 'approved' ? 'approved' : decision === 'returned' ? 'returned' : decision === 'rejected' ? 'rejected' : 'held', action: `নথি ${labels[decision]}`, note: comment || undefined });
      toast.success(`নথি ${labels[decision]} — ইতিহাস হালনাগাদ হয়েছে।`);
    },
    comment(pcr: string, text: string) {
      updateCase(pcr, (c) => c, { ...by, key: 'comment', action: 'মন্তব্য সংযুক্ত', note: text });
      toast.success('মন্তব্য সংযুক্ত হয়েছে।');
    },
    prepareLetter(pcr: string): boolean {
      const c = getCase(pcr);
      if (!c) return false;
      const blockers = letterBlockers(c);
      if (blockers.length) { toast.error(`পত্র প্রস্তুত করা যাবে না: ${blockers.join('; ')}`); return false; }
      const seq = 220 + Number(pcr.slice(-2));
      const div = c.division.replace('ট্রাফিক-', '').replace(' বিভাগ', '');
      const code = ({ তেজগাঁও: 'TEJ', মিরপুর: 'MIR', গুলশান: 'GUL', রমনা: 'RAM', উত্তরা: 'UTT', মতিঝিল: 'MOT', ওয়ারী: 'WAR', লালবাগ: 'LAL' } as Record<string, string>)[div] ?? 'DMP';
      updateCase(pcr, (x) => ({ ...x, letter: { number: `DMP/TR-${code}/REL/2026/${String(seq).padStart(4, '0')}`, status: 'draft', preparedAt: nowIso(), qrToken: qrToken(pcr) } }),
        { ...by, key: 'letter_prepared', action: 'অবমুক্তি পত্রের খসড়া প্রস্তুত' });
      toast.success('অবমুক্তি পত্রের খসড়া প্রস্তুত হয়েছে।');
      return true;
    },
    signLetter(pcr: string) {
      updateCase(pcr, (c) => (c.letter ? { ...c, letter: { ...c.letter, status: 'signed', signedAt: nowIso(), signedBy: user.name } } : c),
        { ...by, key: 'signed', action: 'অবমুক্তি পত্রে অনুমোদিত স্বাক্ষর (সিমুলেটেড প্রমাণীকরণ)' });
      toast.success('স্বাক্ষর অনুমোদন সম্পন্ন (সিমুলেটেড — প্রোডাকশন-গ্রেড ডিজিটাল স্বাক্ষর নয়)।');
    },
    issueLetter(pcr: string) {
      updateCase(pcr, (c) => (c.letter ? { ...c, letter: { ...c.letter, status: 'issued', issuedAt: nowIso() } } : c),
        { ...by, key: 'issued', action: 'অবমুক্তি পত্র ইস্যু ও QR সক্রিয়' });
      toast.success('পত্র ইস্যু হয়েছে — QR এখন বৈধ। মালিক অ্যাপে বিজ্ঞপ্তি গেছে।');
    },
    cancelLetter(pcr: string, reason: string) {
      updateCase(pcr, (c) => (c.letter ? { ...c, letter: { ...c.letter, status: 'cancelled' } } : c),
        { ...by, key: 'comment', action: 'অবমুক্তি পত্র বাতিল — QR অকার্যকর', note: reason });
      toast.success('পত্র বাতিল করা হয়েছে। QR আর বৈধ নয়।');
    },
    assign(pcr: string, officer: string) {
      updateCase(pcr, (c) => c, { ...by, key: 'comment', action: `কেস দায়িত্ব অর্পণ — ${officer}` });
      toast.success(`কেসটি ${officer}-কে অর্পণ করা হয়েছে।`);
    },
    txn: () => `TXN-DMP-2610${randomDigits(7)}`,
  };
}
